import { createFileRoute, Link } from '@tanstack/react-router';
import { useEffect, useMemo, useState } from 'react';
import { Mail, MessageSquare, AlertCircle, FileText, Inbox as InboxIcon, ChevronLeft } from 'lucide-react';
import { Sidebar } from '@/components/layout/sidebar';
import { Button } from '@/components/ui/button';
import { Skeleton } from '@/components/ui/skeleton';
import { EmptyState } from '@/components/ui/empty-state';
import {
  useInboxThread,
  useInboxThreads,
  useMarkRead,
  useSendReply,
  useAIDraft,
  useGmailConnectionStatus,
  useStartGmailOAuth,
  type InboxCall,
  type InboxMessage,
} from '@/features/inbox/queries';
import {
  useSmsThreads,
  useSmsThread,
  useMarkSmsRead,
  useSendSmsToThread,
  type SmsThread,
} from '@/features/sms/queries';
import { useUpsertPreferences, useUserPreferences } from '@/features/preferences/queries';
import {
  useTemplates,
  buildTemplateContext,
  renderTemplateField,
} from '@/features/templates/queries';
import { useCurrentUser } from '@/features/auth/use-current-user';
import { useCurrentPipeline } from '@/features/preferences/use-pipeline';
import { cn } from '@/lib/utils';

type ActiveThread = { kind: 'email'; id: string } | { kind: 'sms'; id: string } | null;

type InboxFilterValue = 'all' | 'unread';
const INBOX_FILTERS: { value: InboxFilterValue; label: string }[] = [
  { value: 'all', label: 'All' },
  { value: 'unread', label: 'Unread' },
];

export const Route = createFileRoute('/_auth/inbox')({ component: InboxPage });

function InboxPage(): JSX.Element {
  const [active, setActive] = useState<ActiveThread>(null);
  const [composer, setComposer] = useState('');
  const [filter, setFilter] = useState<InboxFilterValue>('all');
  const { data: threadsData, isLoading: threadsLoading, error: threadsError } = useInboxThreads();
  const { data: smsThreadsData } = useSmsThreads();
  const activeEmailId = active?.kind === 'email' ? active.id : null;
  const activeSmsId = active?.kind === 'sms' ? active.id : null;
  const { data: detail, isLoading: detailLoading } = useInboxThread(activeEmailId);
  const { data: smsDetail, isLoading: smsDetailLoading } = useSmsThread(activeSmsId);
  const { data: prefs } = useUserPreferences();
  const { data: gmailStatus } = useGmailConnectionStatus();
  const { data: me } = useCurrentUser();
  const { pipeline: activePipeline } = useCurrentPipeline();
  const { data: emailTemplatesData } = useTemplates('email', activePipeline);
  const { data: smsTemplatesData } = useTemplates('sms', activePipeline);
  const [showTemplatePicker, setShowTemplatePicker] = useState(false);
  const upsertPrefs = useUpsertPreferences();
  const send = useSendReply();
  const sendSms = useSendSmsToThread();
  const markRead = useMarkRead();
  const markSmsRead = useMarkSmsRead();
  const aiDraft = useAIDraft();
  const showConnectBanner = gmailStatus !== undefined && !gmailStatus.connected;

  useEffect(() => {
    if (prefs?.inbox_filter === 'all' || prefs?.inbox_filter === 'unread') {
      setFilter(prefs.inbox_filter);
    }
  }, [prefs?.inbox_filter]);

  // Merged threads: email + SMS, sorted by last_message_at desc.
  type MergedThread =
    | { kind: 'email'; id: string; last_message_at: string | null; unread_count: number; subject: string | null; contact_name: string; contact_email: string | null }
    | { kind: 'sms'; id: string; last_message_at: string | null; unread_count: number; phone_number: string; contact_name: string; preview: string | null };

  const mergedThreads = useMemo<MergedThread[]>(() => {
    const emails: MergedThread[] = (threadsData?.threads ?? []).map((t) => ({
      kind: 'email' as const,
      id: t.id,
      last_message_at: t.last_message_at,
      unread_count: t.unread_count,
      subject: t.subject,
      contact_name: t.contacts
        ? `${t.contacts.first_name ?? ''} ${t.contacts.last_name ?? ''}`.trim() || (t.contacts.email ?? 'Unmatched')
        : 'Unmatched',
      contact_email: t.contacts?.email ?? null,
    }));
    const smsAll: MergedThread[] = (smsThreadsData?.threads ?? []).map((t: SmsThread) => ({
      kind: 'sms' as const,
      id: t.id,
      last_message_at: t.last_message_at,
      unread_count: t.unread_count,
      phone_number: t.phone_number,
      contact_name: t.contacts
        ? `${t.contacts.first_name ?? ''} ${t.contacts.last_name ?? ''}`.trim() || (t.contacts.company_name ?? t.phone_number)
        : t.phone_number,
      preview: t.last_message_preview,
    }));
    const all = [...emails, ...smsAll];
    all.sort((a, b) => {
      const at = a.last_message_at ? new Date(a.last_message_at).getTime() : 0;
      const bt = b.last_message_at ? new Date(b.last_message_at).getTime() : 0;
      return bt - at;
    });
    if (filter === 'unread') return all.filter((t) => t.unread_count > 0);
    return all;
  }, [threadsData?.threads, smsThreadsData?.threads, filter]);

  const onFilterChange = (next: InboxFilterValue): void => {
    setFilter(next);
    upsertPrefs.mutate({ inbox_filter: next });
  };

  useEffect(() => {
    if (active?.kind === 'email') markRead.mutate({ threadId: active.id });
    if (active?.kind === 'sms') markSmsRead.mutate({ threadId: active.id });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [active?.kind, active?.id]);

  const smsContactId = smsDetail?.thread.contact_id ?? null;

  const onSend = async (): Promise<void> => {
    if (!composer.trim()) return;
    if (active?.kind === 'email') {
      await send.mutateAsync({ threadId: active.id, body: composer });
      setComposer('');
    } else if (active?.kind === 'sms') {
      if (!smsContactId) {
        // Need a contact to send via /api/sms/send. If thread has none, surface a friendly message.
        return;
      }
      await sendSms.mutateAsync({ contactId: smsContactId, body: composer });
      setComposer('');
    }
  };

  const onAIDraft = async (): Promise<void> => {
    if (active?.kind !== 'email') return;
    const result = await aiDraft.mutateAsync({ threadId: active.id });
    if (result.draft) setComposer(result.draft);
  };

  return (
    <div className="flex h-screen bg-bg text-text">
      <Sidebar />
      <main className="flex-1 flex flex-col min-h-0">
        {showConnectBanner && (
          <ConnectBanner needsReauth={!!gmailStatus?.needs_reauth} />
        )}
        <div className="flex-1 grid grid-cols-1 md:grid-cols-[320px_1fr] min-h-0">
        {/* Threads list — full width on mobile; hidden once a thread is open. */}
        <aside className={cn('border-r border-border/[0.08] flex-col min-h-0', active ? 'hidden md:flex' : 'flex')}>
          <div className="pl-14 pr-4 md:px-4 py-4 border-b border-border/[0.08]">
            <h1 className="font-display text-xl">Inbox</h1>
            <div className="mt-2 flex items-center gap-1">
              {INBOX_FILTERS.map((f) => (
                <button
                  key={f.value}
                  type="button"
                  onClick={() => onFilterChange(f.value)}
                  className={cn(
                    'rounded px-2 py-0.5 text-[10px] font-medium uppercase tracking-wider transition',
                    filter === f.value
                      ? 'bg-accent/15 text-accent ring-1 ring-accent/40'
                      : 'text-text-mute hover:bg-surface-2 hover:text-text-dim',
                  )}
                >
                  {f.label}
                </button>
              ))}
            </div>
          </div>
          <div className="flex-1 overflow-auto">
            {threadsLoading && <InboxListSkeleton />}
            {threadsError && (
              <div className="p-4 text-danger text-sm">
                {String(threadsError instanceof Error ? threadsError.message : threadsError)}
                {String(threadsError).match(/Gmail/i) && (
                  <div className="mt-2">
                    <Link to="/settings" className="text-accent underline">
                      Connect Gmail in Settings →
                    </Link>
                  </div>
                )}
              </div>
            )}
            {(threadsData || smsThreadsData) && mergedThreads.length === 0 && (
              <EmptyState
                className="py-12"
                icon={InboxIcon}
                title={filter === 'unread' ? 'All caught up' : 'No conversations yet'}
                description={
                  filter === 'unread'
                    ? 'No unread email or SMS threads right now.'
                    : 'Email and SMS conversations will land here as they come in.'
                }
              />
            )}
            {mergedThreads.map((t) => {
              const isActive = active?.kind === t.kind && active.id === t.id;
              const unread = t.unread_count > 0;
              const subline =
                t.kind === 'email'
                  ? (t.subject ?? '(no subject)')
                  : (t.preview ?? t.phone_number);
              return (
                <button
                  key={`${t.kind}-${t.id}`}
                  onClick={() => setActive({ kind: t.kind, id: t.id })}
                  className={`w-full text-left px-4 py-3 border-b border-border/[0.04] transition-all duration-fast hover:-translate-y-px ${
                    isActive ? 'bg-accent/10' : 'hover:bg-surface-hi'
                  }`}
                >
                  <div className="flex items-baseline justify-between gap-2">
                    <span className={cn('text-sm truncate flex items-center gap-1.5', unread ? 'font-semibold text-text' : 'text-text-dim')}>
                      {t.kind === 'email' ? (
                        <Mail className="h-3 w-3 flex-shrink-0 text-accent" />
                      ) : (
                        <MessageSquare className="h-3 w-3 flex-shrink-0 text-accent-2" />
                      )}
                      <span className="truncate">{t.contact_name}</span>
                      {unread && (
                        <span className="flex-shrink-0 rounded-full bg-accent/30 px-1.5 text-[9px] font-mono text-accent">
                          {t.unread_count}
                        </span>
                      )}
                    </span>
                    <span className="text-xs text-text-mute font-mono flex-shrink-0">
                      {t.last_message_at ? formatShortTime(t.last_message_at) : ''}
                    </span>
                  </div>
                  <div className={`text-xs truncate mt-1 ${unread ? 'text-text-dim' : 'text-text-mute'}`}>
                    {subline}
                  </div>
                </button>
              );
            })}
          </div>
        </aside>

        {/* Conversation pane — full width on mobile; hidden until a thread is open. */}
        <section className={cn('flex-col min-h-0 min-w-0', active ? 'flex' : 'hidden md:flex')}>
          {active && (
            <button
              type="button"
              onClick={() => setActive(null)}
              className="md:hidden flex items-center gap-1 border-b border-border/[0.08] pl-14 pr-4 py-3 text-sm text-text-dim hover:text-text"
            >
              <ChevronLeft className="h-4 w-4" /> Back to inbox
            </button>
          )}
          {!active ? (
            <EmptyState
              className="flex-1"
              icon={MessageSquare}
              title="Select a conversation"
              description="Choose an email or SMS thread from the list to read it and reply."
            />
          ) : active.kind === 'email' ? (
            detailLoading || !detail ? (
              <ConversationSkeleton />
            ) : (
            <>
              <div className="px-6 py-4 border-b border-border/[0.08]">
                <div className="font-display text-lg flex items-center gap-2">
                  <Mail className="h-4 w-4 text-accent" />
                  {detail.thread.subject ?? '(no subject)'}
                </div>
                <div className="text-text-dim text-sm">
                  {detail.thread.contacts
                    ? `${detail.thread.contacts.first_name ?? ''} ${detail.thread.contacts.last_name ?? ''}`.trim() ||
                      detail.thread.contacts.email
                    : 'Unmatched contact'}
                </div>
              </div>
              <div className="flex-1 overflow-auto px-6 py-4 space-y-3">
                {sortEntries(detail.messages, detail.calls).map((entry) =>
                  entry.kind === 'message' ? (
                    <MessageRow key={`m-${entry.data.id}`} message={entry.data} />
                  ) : (
                    <CallRow key={`c-${entry.data.id}`} call={entry.data} />
                  ),
                )}
              </div>
              <div className="border-t border-border/[0.08] p-4">
                {showTemplatePicker && (emailTemplatesData?.templates ?? []).length > 0 && (
                  <div className="mb-2 rounded border border-border/[0.16] bg-surface-2 p-2">
                    <div className="text-[10px] uppercase tracking-wider text-text-mute mb-1.5 flex items-center gap-1">
                      <FileText className="h-3 w-3" /> Pick a template
                    </div>
                    <div className="flex flex-wrap gap-1.5">
                      {(emailTemplatesData?.templates ?? []).map((t) => (
                        <button
                          key={t.id}
                          type="button"
                          onClick={() => {
                            const ctx = buildTemplateContext({
                              contact: detail.thread.contacts ?? null,
                              sender: { full_name: me?.fullName ?? null },
                            });
                            setComposer(renderTemplateField(t.body, ctx));
                            setShowTemplatePicker(false);
                          }}
                          className="rounded border border-border/[0.16] px-2 py-1 text-xs text-text-dim hover:border-accent/40 hover:text-text"
                          title={t.description ?? undefined}
                        >
                          {t.name}
                        </button>
                      ))}
                    </div>
                  </div>
                )}
                <textarea
                  value={composer}
                  onChange={(e) => setComposer(e.target.value)}
                  placeholder="Type a reply…"
                  rows={4}
                  className="w-full bg-surface-2 border border-border/[0.16] rounded p-3 text-sm resize-none focus:outline-none focus:border-accent"
                />
                <div className="flex items-center justify-between mt-2 gap-2">
                  <div className="flex gap-2">
                    <Button size="sm" variant="outline" disabled={aiDraft.isPending} onClick={() => void onAIDraft()}>
                      {aiDraft.isPending ? 'Drafting…' : 'AI Draft'}
                    </Button>
                    <Button
                      size="sm"
                      variant="outline"
                      onClick={() => setShowTemplatePicker((v) => !v)}
                      disabled={(emailTemplatesData?.templates ?? []).length === 0}
                    >
                      <FileText className="h-3 w-3 mr-1" />
                      Templates
                    </Button>
                  </div>
                  <Button size="sm" disabled={send.isPending || !composer.trim()} onClick={() => void onSend()}>
                    {send.isPending ? 'Sending…' : 'Send'}
                  </Button>
                </div>
              </div>
            </>
            )
          ) : active.kind === 'sms' ? (
            smsDetailLoading || !smsDetail ? (
              <ConversationSkeleton />
            ) : (
              <>
                <div className="px-6 py-4 border-b border-border/[0.08]">
                  <div className="font-display text-lg flex items-center gap-2">
                    <MessageSquare className="h-4 w-4 text-accent-2" />
                    SMS · <span className="font-mono text-base text-text-dim">{smsDetail.thread.phone_number}</span>
                  </div>
                  <div className="text-text-dim text-sm">
                    {smsDetail.thread.contacts
                      ? `${smsDetail.thread.contacts.first_name ?? ''} ${smsDetail.thread.contacts.last_name ?? ''}`.trim() ||
                        smsDetail.thread.contacts.company_name ||
                        'Unmatched contact'
                      : 'Unmatched contact'}
                  </div>
                </div>
                <div className="flex-1 overflow-auto px-6 py-4 space-y-2">
                  {smsDetail.messages.map((m) => (
                    <SmsMessageRow key={m.id} msg={m} />
                  ))}
                </div>
                <div className="border-t border-border/[0.08] p-4">
                  {!smsContactId && (
                    <div className="mb-2 rounded border border-warn/30 bg-warn/10 px-3 py-2 text-xs text-warn">
                      ⚠ This thread isn't linked to a contact yet. Replies disabled until a contact is matched (auto-match runs on next inbound).
                    </div>
                  )}
                  {showTemplatePicker && (smsTemplatesData?.templates ?? []).length > 0 && (
                    <div className="mb-2 rounded border border-border/[0.16] bg-surface-2 p-2">
                      <div className="text-[10px] uppercase tracking-wider text-text-mute mb-1.5 flex items-center gap-1">
                        <FileText className="h-3 w-3" /> Pick a template
                      </div>
                      <div className="flex flex-wrap gap-1.5">
                        {(smsTemplatesData?.templates ?? []).map((t) => (
                          <button
                            key={t.id}
                            type="button"
                            onClick={() => {
                              const ctx = buildTemplateContext({
                                contact: smsDetail.thread.contacts
                                  ? {
                                      first_name: smsDetail.thread.contacts.first_name,
                                      last_name: smsDetail.thread.contacts.last_name,
                                      company_name: smsDetail.thread.contacts.company_name,
                                    }
                                  : null,
                                sender: { full_name: me?.fullName ?? null },
                              });
                              setComposer(renderTemplateField(t.body, ctx));
                              setShowTemplatePicker(false);
                            }}
                            className="rounded border border-border/[0.16] px-2 py-1 text-xs text-text-dim hover:border-accent/40 hover:text-text"
                            title={t.description ?? undefined}
                          >
                            {t.name}
                          </button>
                        ))}
                      </div>
                    </div>
                  )}
                  <textarea
                    value={composer}
                    onChange={(e) => setComposer(e.target.value)}
                    placeholder="Type SMS reply…"
                    rows={3}
                    className="w-full bg-surface-2 border border-border/[0.16] rounded p-3 text-sm resize-none focus:outline-none focus:border-accent"
                    disabled={!smsContactId}
                  />
                  <div className="flex items-center justify-between mt-2 gap-2">
                    <div className="flex items-center gap-2">
                      <Button
                        size="sm"
                        variant="outline"
                        onClick={() => setShowTemplatePicker((v) => !v)}
                        disabled={(smsTemplatesData?.templates ?? []).length === 0}
                      >
                        <FileText className="h-3 w-3 mr-1" />
                        Templates
                      </Button>
                      <span className="text-[10px] text-text-mute font-mono">
                        {composer.length} chars · ~{Math.max(1, Math.ceil(composer.length / 160))} segment(s)
                      </span>
                    </div>
                    <Button
                      size="sm"
                      disabled={sendSms.isPending || !composer.trim() || !smsContactId}
                      onClick={() => void onSend()}
                    >
                      {sendSms.isPending ? 'Sending…' : 'Send SMS'}
                    </Button>
                  </div>
                </div>
              </>
            )
          ) : null}
        </section>
        </div>
      </main>
    </div>
  );
}

function InboxListSkeleton(): JSX.Element {
  return (
    <div aria-busy="true">
      {Array.from({ length: 7 }).map((_, i) => (
        <div key={i} className="border-b border-border/[0.04] px-4 py-3">
          <div className="flex items-center justify-between gap-2">
            <Skeleton className="h-3 w-1/2" />
            <Skeleton className="h-3 w-10" />
          </div>
          <Skeleton className="mt-2 h-3 w-3/4" />
        </div>
      ))}
    </div>
  );
}

function ConversationSkeleton(): JSX.Element {
  return (
    <div className="flex flex-1 flex-col min-h-0" aria-busy="true">
      <div className="border-b border-border/[0.08] px-6 py-4">
        <Skeleton className="h-4 w-1/3" />
        <Skeleton className="mt-2 h-3 w-1/4" />
      </div>
      <div className="flex-1 space-y-4 overflow-auto px-6 py-4">
        <div className="max-w-md">
          <Skeleton className="h-3 w-24" />
          <Skeleton className="mt-1 h-16 w-full" />
        </div>
        <div className="ml-auto max-w-md">
          <Skeleton className="ml-auto h-3 w-24" />
          <Skeleton className="mt-1 h-12 w-full" />
        </div>
        <div className="max-w-md">
          <Skeleton className="h-3 w-24" />
          <Skeleton className="mt-1 h-20 w-full" />
        </div>
      </div>
    </div>
  );
}

function SmsMessageRow({ msg }: { msg: import('@/features/sms/queries').SmsMessage }): JSX.Element {
  const outbound = msg.direction === 'outbound';
  const at = msg.sent_at ?? msg.created_at;
  return (
    <div className={`max-w-2xl ${outbound ? 'ml-auto' : ''}`}>
      <div className="flex items-baseline justify-between gap-3 text-xs text-text-mute mb-1">
        <span className="font-mono">{outbound ? msg.from_number : msg.to_number}</span>
        <span className="font-mono">{at ? formatShortTime(at) : ''}</span>
      </div>
      <div
        className={`p-3 rounded text-sm whitespace-pre-wrap ${
          outbound
            ? 'bg-accent-2/15 border border-accent-2/30'
            : 'bg-surface-2 border border-border/[0.08]'
        }`}
      >
        {msg.body}
      </div>
    </div>
  );
}

function ConnectBanner({ needsReauth }: { needsReauth: boolean }): JSX.Element {
  const startOAuth = useStartGmailOAuth();
  return (
    <div
      className={cn(
        'flex items-center gap-3 border-b px-6 py-2.5 text-sm',
        needsReauth
          ? 'border-warn/40 bg-warn/10 text-warn'
          : 'border-accent/30 bg-accent/[0.06] text-text',
      )}
    >
      {needsReauth ? (
        <AlertCircle className="h-4 w-4 flex-shrink-0" />
      ) : (
        <Mail className="h-4 w-4 flex-shrink-0 text-accent" />
      )}
      <span className="flex-1">
        {needsReauth
          ? 'Gmail authorization expired. Reconnect to keep sending and receiving.'
          : 'Connect your Gmail to send and receive email from inside the CRM.'}
      </span>
      <Button
        size="sm"
        disabled={startOAuth.isPending}
        onClick={() => void startOAuth.mutateAsync()}
      >
        {startOAuth.isPending
          ? 'Opening Google…'
          : needsReauth
            ? 'Reconnect Gmail'
            : 'Connect Gmail'}
      </Button>
      <Link
        to="/settings"
        className="text-xs text-text-mute underline hover:text-text-dim"
      >
        Manage in Settings
      </Link>
    </div>
  );
}

function MessageRow({ message }: { message: InboxMessage }): JSX.Element {
  const outbound = message.direction === 'outbound';
  return (
    <div className={`max-w-3xl ${outbound ? 'ml-auto' : ''}`}>
      <div className="flex items-baseline justify-between gap-3 text-xs text-text-mute mb-1">
        <span>{message.from_email ?? ''}</span>
        <span className="font-mono">{message.sent_at ? formatShortTime(message.sent_at) : ''}</span>
      </div>
      <div
        className={`p-3 rounded text-sm whitespace-pre-wrap ${
          outbound ? 'bg-accent/10 border border-accent/30' : 'bg-surface-2 border border-border/[0.08]'
        }`}
      >
        {message.body_plain ?? message.snippet ?? ''}
      </div>
    </div>
  );
}

function CallRow({ call }: { call: InboxCall }): JSX.Element {
  const mins = Math.round((call.duration_seconds ?? 0) / 60);
  return (
    <div className="max-w-3xl mx-auto">
      <div className="flex items-baseline justify-between gap-3 text-xs text-text-mute mb-1">
        <span>Call · {mins} min</span>
        <span className="font-mono">{call.started_at ? formatShortTime(call.started_at) : ''}</span>
      </div>
      <div className="p-3 rounded text-sm bg-surface-2 border border-border/[0.08]">
        {call.ai_summary ?? '(no AI summary)'}
        {call.recording_url && (
          <a href={call.recording_url} target="_blank" rel="noreferrer" className="block mt-2 text-accent text-xs">
            Listen →
          </a>
        )}
      </div>
    </div>
  );
}

type SortedEntry =
  | { kind: 'message'; at: string; data: InboxMessage }
  | { kind: 'call'; at: string; data: InboxCall };

function sortEntries(messages: InboxMessage[], calls: InboxCall[]): SortedEntry[] {
  const m: SortedEntry[] = messages
    .filter((x): x is InboxMessage & { sent_at: string } => !!x.sent_at)
    .map((data) => ({ kind: 'message', at: data.sent_at as string, data }));
  const c: SortedEntry[] = calls
    .filter((x): x is InboxCall & { started_at: string } => !!x.started_at)
    .map((data) => ({ kind: 'call', at: data.started_at as string, data }));
  return [...m, ...c].sort((a, b) => new Date(a.at).getTime() - new Date(b.at).getTime());
}

function formatShortTime(iso: string): string {
  const d = new Date(iso);
  const now = new Date();
  const sameDay = d.toDateString() === now.toDateString();
  if (sameDay) return d.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' });
  return d.toLocaleDateString([], { month: 'short', day: 'numeric' });
}
